import { Icon } from '../ds';
import { formatTime } from '../lib/time';

/**
 * Time field.
 *
 * The prototype showed a static "今 · 13:07". A real log has to be editable —
 * you write the 3am feed down at 3:40 — so this is a native time input, which
 * also gets the platform's own picker and keyboard on a phone. The "いま"
 * button snaps back to the current time.
 */

function toInputValue(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Apply an "HH:mm" string to the same calendar day as `base`. */
export function applyTimeString(base: Date, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number);
  const out = new Date(base);
  if (!Number.isNaN(h)) out.setHours(h, Number.isNaN(m) ? 0 : m, 0, 0);
  return out;
}

function toDateInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Apply a "YYYY-MM-DD" string to `base`, preserving the clock time. */
export function applyDateString(base: Date, ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  const out = new Date(base);
  if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) out.setFullYear(y, m - 1, d);
  return out;
}

/**
 * Date field — the calendar half of TimeField.
 *
 * Same native-input philosophy: a real date input gets the platform's own
 * picker on a phone. Exists for the sleep rows, where the day cannot always
 * be inferred — backfilling last night, or correcting which night an entry
 * belongs to.
 */
export function DateField({
  value,
  onChange,
  disabled = false,
}: {
  value: Date;
  onChange: (next: Date) => void;
  disabled?: boolean;
}) {
  return (
    <input
      type="date"
      value={toDateInputValue(value)}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value) onChange(applyDateString(value, e.target.value));
      }}
      aria-label="日付"
      style={{
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        fontSize: 'var(--text-md)',
        color: disabled ? 'var(--text-subtle)' : 'var(--text)',
        background: 'transparent',
        border: '1px solid transparent',
        borderRadius: 'var(--radius-xs)',
        padding: '2px 4px',
        textAlign: 'right',
      }}
    />
  );
}

export function TimeField({
  value,
  onChange,
  disabled = false,
  showNowButton = true,
}: {
  value: Date;
  onChange: (next: Date) => void;
  disabled?: boolean;
  showNowButton?: boolean;
}) {
  const isNow = Math.abs(Date.now() - value.getTime()) < 60_000;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      {isNow ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-subtle)', fontWeight: 600 }}>今</span>
      ) : showNowButton && !disabled ? (
        <button
          type="button"
          onClick={() => onChange(new Date())}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-link)',
            fontSize: 'var(--text-xs)',
            fontWeight: 700,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <Icon name="RotateCcw" size={12} />
          いま
        </button>
      ) : null}

      <input
        type="time"
        value={toInputValue(value)}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value) onChange(applyTimeString(value, e.target.value));
        }}
        aria-label="時刻"
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 'var(--text-md)',
          color: disabled ? 'var(--text-subtle)' : 'var(--text)',
          background: 'transparent',
          border: '1px solid transparent',
          borderRadius: 'var(--radius-xs)',
          padding: '2px 4px',
          textAlign: 'right',
        }}
      />
    </div>
  );
}

/** Read-only mono time, for rows that display rather than edit. */
export function TimeText({ value, muted = false }: { value: Date; muted?: boolean }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        color: muted ? 'var(--text-subtle)' : 'var(--text)',
      }}
    >
      {formatTime(value)}
    </span>
  );
}
